/**
 * Minimal XLSX (SpreadsheetML) writer.
 *
 * The exports used to be an HTML table saved with an `.xls` extension. Excel
 * parses that, but first warns "The file format and extension of '...' don't
 * match. The file could be corrupted or unsafe", and on machines where the
 * Office policy blocks mismatched extensions it refuses to open the file at
 * all. This builds a real `.xlsx` package instead: a ZIP of the OOXML parts,
 * which every spreadsheet (Excel, LibreOffice, Google Sheets, Numbers) opens
 * without complaining.
 *
 * Every cell is written as an inline string, so long codes (consecutivo, CABYS,
 * cédula) keep their leading zeros and never turn into scientific notation —
 * the problem the old `mso-number-format` hack worked around.
 */

import { strToU8, zipSync } from 'fflate'

export interface XlsxSheet {
  /** Tab name. Sanitised to what Excel accepts. */
  name: string
  headers: string[]
  rows: string[][]
  /** Render the last row in bold, for a totals row. */
  boldLastRow?: boolean
}

const NS_MAIN = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main'
const NS_REL_DOC = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
const NS_PKG_REL = 'http://schemas.openxmlformats.org/package/2006/relationships'

// Characters that are simply not allowed in XML 1.0, at any escaping level.
// eslint-disable-next-line no-control-regex
const INVALID_XML_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g

function xmlEscape(value: string): string {
  return value
    .replace(INVALID_XML_CHARS, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/** 0 -> A, 25 -> Z, 26 -> AA ... */
function columnName(index: number): string {
  let name = ''
  let n = index
  while (n >= 0) {
    name = String.fromCharCode(65 + (n % 26)) + name
    n = Math.floor(n / 26) - 1
  }
  return name
}

/** Excel rejects : \ / ? * [ ] in a tab name, and caps it at 31 characters. */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[:\/?*[\]]/g, ' ').trim()
  return (cleaned || 'Datos').slice(0, 31)
}

function cellXml(ref: string, value: string, bold: boolean): string {
  const style = bold ? ' s="1"' : ''
  if (value === '') return `<c r="${ref}"${style}/>`
  return (
    `<c r="${ref}"${style} t="inlineStr">` +
    `<is><t xml:space="preserve">${xmlEscape(value)}</t></is></c>`
  )
}

function rowXml(cells: string[], rowNumber: number, bold: boolean): string {
  const body = cells
    .map((value, i) => cellXml(`${columnName(i)}${rowNumber}`, value ?? '', bold))
    .join('')
  return `<row r="${rowNumber}">${body}</row>`
}

/**
 * Column widths, so the reader does not land on a sheet full of `####`.
 * Excel's width unit is roughly one character of the default font.
 */
function colsXml(headers: string[], rows: string[][]): string {
  if (headers.length === 0) return ''
  const widths = headers.map((header, i) => {
    let longest = header.length
    for (const row of rows) {
      const length = (row[i] ?? '').length
      if (length > longest) longest = length
    }
    return Math.min(60, Math.max(10, longest + 2))
  })
  const cols = widths
    .map((width, i) => `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`)
    .join('')
  return `<cols>${cols}</cols>`
}

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="${NS_PKG_REL}">
<Relationship Id="rId1" Type="${NS_REL_DOC}/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="${NS_PKG_REL}">
<Relationship Id="rId1" Type="${NS_REL_DOC}/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="${NS_REL_DOC}/styles" Target="styles.xml"/>
</Relationships>`

// Style 0 = default, style 1 = bold (headers and the totals row).
// The two fills are not decoration: Excel repairs the file if index 0 is not
// "none" and index 1 is not "gray125".
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="${NS_MAIN}">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`

/** Build a single-sheet .xlsx workbook. */
export function buildXlsx({ name, headers, rows, boldLastRow = false }: XlsxSheet): Uint8Array {
  const lastRowIndex = rows.length - 1
  const sheetRows = [
    rowXml(headers, 1, true),
    ...rows.map((row, i) => rowXml(row, i + 2, boldLastRow && i === lastRowIndex)),
  ].join('')

  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="${NS_MAIN}">${colsXml(headers, rows)}<sheetData>${sheetRows}</sheetData></worksheet>`

  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL_DOC}">
<sheets><sheet name="${xmlEscape(sanitizeSheetName(name))}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`

  return zipSync(
    {
      '[Content_Types].xml': strToU8(CONTENT_TYPES),
      '_rels/.rels': strToU8(ROOT_RELS),
      'xl/workbook.xml': strToU8(workbook),
      'xl/_rels/workbook.xml.rels': strToU8(WORKBOOK_RELS),
      'xl/styles.xml': strToU8(STYLES),
      'xl/worksheets/sheet1.xml': strToU8(sheet),
    },
    { level: 6 }
  )
}

export const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
